import type { Agent } from "@/types/agent/agent";
import type { DistributionWidgetConfig, DistributionWidgetKind } from "@/types/distribution-widgets";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { getBrandThemeFromColors } from "@/utils/branding/genieBrandTheme";
import { getEmailWidgetButtonBackground } from "@/lib/emailWidgetStyle";
import { buildHelpGenieAttributionUrl } from "@/lib/helpGenieAttributionLink";

const webUrl = (value: string, baseUrl: string): string | null => {
  try {
    const url = new URL(value, baseUrl);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

const brandColor = (value: string | null | undefined, fallback: string): string =>
  value && /^#[\da-f]{6}$/i.test(value) ? value : fallback;

export const getDistributionWidgetConfig = (
  genie: Agent,
  kind: DistributionWidgetKind,
  baseUrl: string,
): DistributionWidgetConfig => {
  const branding = {
    ...DEFAULT_BRANDING,
    ...genie.branding,
    primaryColor: brandColor(genie.branding?.primaryColor, DEFAULT_BRANDING.primaryColor),
    secondaryColor: brandColor(genie.branding?.secondaryColor, genie.branding?.primaryColor || DEFAULT_BRANDING.primaryColor),
  };
  branding.secondaryColor = brandColor(branding.secondaryColor, branding.primaryColor);
  const theme = getBrandThemeFromColors(branding);
  const phoneNumber = genie.phone?.number?.trim() || null;
  const dialNumber = phoneNumber?.replace(/[\s().-]/g, "") || "";
  const slug = genie.url_name || genie.page?.url_name;
  const publicPage = genie.is_active && (genie.is_public || genie.is_marketplace_genie);
  let href: string | null = null;
  let unavailableReason: string | null = null;

  if (kind === "phone") {
    if (!phoneNumber) unavailableReason = "Add a phone number in this Genie’s Phone settings to get your widget code.";
    else if (!/^\+[1-9]\d{6,14}$/.test(dialNumber)) unavailableReason = "Check the Genie’s phone number includes its international country code.";
    else href = `tel:${dialNumber}`;
  } else if (!slug) {
    unavailableReason = "Finish setting up this Genie’s page to get your QR widget.";
  } else if (!publicPage) {
    unavailableReason = "Make this Genie active and public before sharing its QR widget.";
  } else {
    href = webUrl(`/live-agent/${encodeURIComponent(slug)}?ref=qr`, baseUrl);
    if (!href) unavailableReason = "Check the website address in your app settings before sharing this widget.";
  }

  const attributionLogoUrl = webUrl("/helpgenie_logo.png", baseUrl) || "";
  return {
    kind,
    genieName: genie.name,
    logoUrl: webUrl(genie.branding?.logoUrl || genie.branding?.customAvatarUrl || "/helpgenie_logo.png", baseUrl) || attributionLogoUrl,
    attributionLogoUrl,
    attributionUrl: buildHelpGenieAttributionUrl(slug),
    hideAttribution: branding.hideAttribution ?? false,
    accentBackground: theme.gradient,
    buttonBackground: getEmailWidgetButtonBackground(branding),
    phoneNumber,
    href,
    unavailableReason,
  };
};
